import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Eye, Share2, XCircle, Clock, Link as LinkIcon,
  Search, Lock, Users, FileText, X,
} from 'lucide-react'
import { Card, KPICard, Tooltip } from '../ui'
import { sharedReportService } from '../../services/sharedReportService'
import { SharedReportListItem, SharedReportsSummary, ShareStatus } from '../../types/sharedReport'

interface Props {
  customerId: string | null
  reloadSignal?: number
  onView?: (item: SharedReportListItem) => void
  onShare?: (item: SharedReportListItem) => void
}

// "Mon D, YYYY" — used for the Sent and Expires columns.
const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'

// Creation timestamp under the report name: mm/dd/yyyy h:mm AM/PM.
const fmtDateTime = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'

const initials = (r: { name?: string; email: string }): string =>
  (r.name
    ? r.name.split(' ').map((p) => p[0]).slice(0, 2).join('')
    : r.email.slice(0, 2)
  ).toUpperCase()

// Stable-ish avatar tint from the email — greyscale-friendly accent set (not per-status color).
const AV_TINTS = ['#4f46e5', '#0891b2', '#059669', '#7c3aed', '#d97706', '#e11d48']
const tintFor = (email: string): string =>
  AV_TINTS[[...email].reduce((a, c) => a + c.charCodeAt(0), 0) % AV_TINTS.length]

const STATUS: Record<ShareStatus, { label: string; cls: string }> = {
  active: { label: 'Active', cls: 'bg-emerald-50 text-emerald-700' },
  expiring: { label: 'Expiring', cls: 'bg-amber-50 text-amber-700' },
  revoked: { label: 'Revoked', cls: 'bg-rose-50 text-rose-700' },
  draft: { label: 'Saved · not sent', cls: 'bg-slate-100 text-slate-600' },
}
const DOT: Record<ShareStatus, string> = { active: '#059669', expiring: '#b45309', revoked: '#e11d48', draft: '#94a3b8' }

const StatusPill: React.FC<{ s: ShareStatus }> = ({ s }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS[s].cls}`}>
    <span className="h-1.5 w-1.5 rounded-full" style={{ background: DOT[s] }} />
    {STATUS[s].label}
  </span>
)

// Greyscale action button — inherits gray, goes blue (reports accent) on hover.
const ActionBtn: React.FC<{ title: string; onClick?: () => void; children: React.ReactNode }> = ({ title, onClick, children }) => (
  <Tooltip content={title} placement="top">
    <button
      type="button"
      aria-label={title}
      onClick={onClick}
      className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-400 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50 transition-colors"
    >
      {children}
    </button>
  </Tooltip>
)

const AvatarStack: React.FC<{ recipients: SharedReportListItem['recipients'] }> = ({ recipients }) => {
  if (recipients.length === 0) return <span className="text-xs text-gray-400">—</span>
  const shown = recipients.slice(0, 4)
  const extra = recipients.length - shown.length
  return (
    <div className="flex items-center">
      <div className="flex">
        {shown.map((r, i) => (
          <span
            key={i}
            title={r.name || r.email}
            className="h-6 w-6 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-bold text-white"
            style={{ background: tintFor(r.email), marginLeft: i === 0 ? 0 : -7 }}
          >
            {initials(r)}
          </span>
        ))}
      </div>
      {extra > 0 && <span className="text-xs text-gray-500 ml-1.5">+{extra}</span>}
    </div>
  )
}

const SharedReportsTab: React.FC<Props> = ({ customerId, reloadSignal, onView, onShare }) => {
  const [summary, setSummary] = useState<SharedReportsSummary | null>(null)
  const [items, setItems] = useState<SharedReportListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  const load = useCallback(() => {
    if (!customerId) { setItems([]); setSummary(null); setLoading(false); return }
    setLoading(true)
    sharedReportService.listSharedReports(customerId)
      .then((res) => { setSummary(res.summary); setItems(res.items); setLoading(false) })
      .catch(() => { setItems([]); setLoading(false) })
  }, [customerId])
  useEffect(() => { load() }, [load, reloadSignal])

  const handleRevoke = async (shareId: string) => {
    if (!customerId) return
    try { await sharedReportService.revoke(shareId, customerId) } finally { load() }
  }
  const handleExtend = async (shareId: string) => {
    if (!customerId) return
    try { await sharedReportService.extend(shareId, customerId, 90) } finally { load() }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return q ? items.filter((i) => i.title.toLowerCase().includes(q) || i.reportTypeLabel.toLowerCase().includes(q)) : items
  }, [items, search])

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard label="Active links" value={summary?.activeLinks ?? 0} icon={LinkIcon} colorScheme="blue" loading={loading} />
        <KPICard label="Views (30d)" value={summary?.views30d ?? 0} icon={Eye} colorScheme="indigo" loading={loading} />
        <KPICard label="Expiring soon" value={summary?.expiringSoon ?? 0} icon={Clock} colorScheme="amber" loading={loading} />
        <KPICard label="Recipients reached" value={summary?.recipientsReached ?? 0} icon={Users} colorScheme="slate" loading={loading} />
      </div>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search saved reports..."
              className="pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[860px]">
            <thead>
              <tr>
                {['Report', 'Status', 'Distribution', 'Views', 'Sent', 'Expires', 'Actions'].map((h, i) => (
                  <th key={i} className={`text-[10px] uppercase tracking-wider text-gray-600 font-semibold px-4 py-2.5 bg-gray-50/70 border-b border-gray-100 ${i === 6 ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-400">No saved reports yet.</td></tr>
              ) : filtered.map((it) => {
                return (
                  <tr key={it.savedReportId} className="hover:bg-gray-50/60 group">
                    <td className="px-4 py-3.5 border-b border-gray-100">
                      <div className="flex items-center gap-2.5 font-semibold text-gray-900 text-xs">
                        <span className="h-2 w-2 rounded-full flex-none" style={{ background: DOT[it.status] }} />
                        {it.title}
                      </div>
                      <div className="text-[11.5px] text-gray-500 mt-0.5 ml-[18px] flex items-center gap-1.5">
                        {it.reportTypeLabel}
                        {it.hasPassword && <span className="inline-flex items-center gap-0.5 text-gray-400"><Lock className="h-3 w-3" /> password</span>}
                      </div>
                      <div className="text-[11.5px] text-gray-400 mt-0.5 ml-[18px]">created {fmtDateTime(it.dataAsOf)}</div>
                    </td>
                    <td className="px-4 py-3.5 border-b border-gray-100"><StatusPill s={it.status} /></td>
                    <td className="px-4 py-3.5 border-b border-gray-100"><AvatarStack recipients={it.recipients} /></td>
                    <td className="px-4 py-3.5 border-b border-gray-100 text-xs text-gray-700 tabular-nums">{it.status === 'draft' ? '—' : it.viewCount}</td>
                    <td className="px-4 py-3.5 border-b border-gray-100 text-xs text-gray-500 tabular-nums">{fmtDate(it.sentAt)}</td>
                    <td className={`px-4 py-3.5 border-b border-gray-100 text-xs tabular-nums ${it.status === 'expiring' ? 'text-amber-600 font-semibold' : 'text-gray-500'}`}>
                      {it.status === 'revoked' ? `revoked ${fmtDate(it.expiresAt)}` : it.status === 'draft' ? '—' : fmtDate(it.expiresAt)}
                    </td>
                    <td className="px-4 py-3.5 border-b border-gray-100">
                      <div className="flex gap-1.5 justify-end opacity-60 group-hover:opacity-100 transition-opacity">
                        {it.status === 'draft' ? (
                          <>
                            <ActionBtn title="View" onClick={() => onView?.(it)}><Eye className="h-4 w-4" /></ActionBtn>
                            <ActionBtn title="Share" onClick={() => onShare?.(it)}><Share2 className="h-4 w-4" /></ActionBtn>
                          </>
                        ) : it.status === 'revoked' ? (
                          <ActionBtn title="View" onClick={() => onView?.(it)}><Eye className="h-4 w-4" /></ActionBtn>
                        ) : (
                          <>
                            <ActionBtn title="View" onClick={() => onView?.(it)}><Eye className="h-4 w-4" /></ActionBtn>
                            {it.status === 'expiring' && <ActionBtn title="Extend 90 days" onClick={() => it.shareId && handleExtend(it.shareId)}><Clock className="h-4 w-4" /></ActionBtn>}
                            <ActionBtn title="Revoke link" onClick={() => it.shareId && handleRevoke(it.shareId)}><XCircle className="h-4 w-4" /></ActionBtn>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-center text-xs text-gray-400 flex items-center justify-center gap-1.5">
        <FileText className="h-3.5 w-3.5" />
        Snapshots are frozen at save — everyone on the distribution sees the same report.
      </p>
    </div>
  )
}

export default SharedReportsTab
